import { env } from "cloudflare:workers";
import { createServerFn } from "@tanstack/react-start";
import { type MathpixResult, recognizeMath } from "#/lib/mathpix";
import { getImage, type ImageKind, putImage } from "#/lib/r2";

const MAX_BYTES = 4_000_000;

export const uploadImage = createServerFn({ method: "POST" })
	.inputValidator((data: unknown) => {
		if (!(data instanceof FormData)) {
			throw new Error("uploadImage expects FormData");
		}
		const kind = data.get("kind");
		const file = data.get("file");
		if (kind !== "question" && kind !== "workingOut") {
			throw new Error("kind must be 'question' or 'workingOut'");
		}
		if (!(file instanceof File)) {
			throw new Error("file is required");
		}
		return { kind: kind as ImageKind, file };
	})
	.handler(async ({ data }) => {
		if (data.file.size > MAX_BYTES) {
			throw new Error(
				`Image too large (${(data.file.size / 1_000_000).toFixed(1)}MB > 4MB)`,
			);
		}
		const bytes = new Uint8Array(await data.file.arrayBuffer());
		const contentType = data.file.type || "image/png";
		const key = await putImage(data.kind, bytes, contentType);
		return { key };
	});

export const recognizeWorkingOut = createServerFn({ method: "POST" })
	.inputValidator((data: unknown) => {
		if (
			typeof data !== "object" ||
			data === null ||
			!("key" in data) ||
			typeof (data as { key: unknown }).key !== "string"
		) {
			throw new Error("expected { key: string }");
		}
		return { key: (data as { key: string }).key };
	})
	.handler(async ({ data }): Promise<MathpixResult> => {
		const bytes = await getImage(data.key);
		if (!bytes) throw new Error(`R2 object not found: ${data.key}`);
		return await recognizeMath(bytes);
	});

type SaveQuestionInput = {
	questionKey: string;
	workingOutKey: string;
	finalLatex: string;
	mathpixRawJson: string;
	mathpixRequestId: string | null;
	confidence: number | null;
	isHandwritten: boolean | null;
};

export const saveQuestion = createServerFn({ method: "POST" })
	.inputValidator((data: unknown): SaveQuestionInput => {
		if (typeof data !== "object" || data === null) {
			throw new Error("expected object");
		}
		const d = data as Record<string, unknown>;
		const requireString = (k: string): string => {
			const v = d[k];
			if (typeof v !== "string" || v.length === 0) {
				throw new Error(`${k} required`);
			}
			return v;
		};
		return {
			questionKey: requireString("questionKey"),
			workingOutKey: requireString("workingOutKey"),
			finalLatex: requireString("finalLatex"),
			mathpixRawJson: requireString("mathpixRawJson"),
			mathpixRequestId:
				typeof d.mathpixRequestId === "string" ? d.mathpixRequestId : null,
			confidence: typeof d.confidence === "number" ? d.confidence : null,
			isHandwritten:
				typeof d.isHandwritten === "boolean" ? d.isHandwritten : null,
		};
	})
	.handler(async ({ data }) => {
		const result = await env.DB.prepare(
			`INSERT INTO questions
				(question_image_key, working_out_image_key, mathpix_request_id,
				 mathpix_raw_json, final_latex, confidence, is_handwritten, created_at)
			 VALUES (?, ?, ?, ?, ?, ?, ?, ?)`,
		)
			.bind(
				data.questionKey,
				data.workingOutKey,
				data.mathpixRequestId,
				data.mathpixRawJson,
				data.finalLatex,
				data.confidence,
				data.isHandwritten === null ? null : data.isHandwritten ? 1 : 0,
				Date.now(),
			)
			.run();
		return { id: result.meta.last_row_id ?? null };
	});
